import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

import { trackCTAClick } from '../lib/analytics';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5675ca92-cfaf-420f-8f00-5e4bce960ecc/artifacts/51x1a7vi_CloneIA-png.png";

const resourcesMenu = [
  {
    category: 'Comprendre',
    links: [
      { to: '/retrouver-information-entreprise', label: 'Retrouver une information' },
      { to: '/retrouver-information-document-entreprise', label: 'Retrouver une info dans un document' },
      { to: '/perte-information-entreprise', label: 'Perte d\'information' },
    ],
  },
  {
    category: 'Approche',
    links: [
      { to: '/centraliser-information-entreprise', label: 'Centraliser l\'information' },
      { to: '/clone-collaborateur-entreprise', label: 'Clone collaborateur' },
    ],
  },
  {
    category: 'Vision',
    links: [
      { to: '/entreprise-interrogeable', label: 'Entreprise interrogeable' },
      { to: '/memoire-entreprise-futur-travail', label: 'Mémoire d\'entreprise' },
      { to: '/blog', label: 'Blog' },
    ],
  },
  {
    category: 'Entreprise',
    links: [
      { to: '/equipe', label: 'Équipe' },
      { to: '/contact', label: 'Contact' },
    ],
  },
];

const internalNavLinks = [
  { to: '/', label: 'Accueil' },
  { to: '/clone-collaborateur-entreprise', label: 'Clone collaborateur' },
  { to: '/entreprise-interrogeable', label: 'Entreprise interrogeable' },
  { to: '/memoire-entreprise-futur-travail', label: 'Mémoire d\'entreprise' },
  { to: '/blog', label: 'Blog' },
];

const internalDropdownLinks = [
  { to: '/retrouver-information-entreprise', label: 'Retrouver une information' },
  { to: '/retrouver-information-document-entreprise', label: 'Information dans un document' },
  { to: '/perte-information-entreprise', label: 'Perte d\'information' },
  { to: '/centraliser-information-entreprise', label: 'Centraliser l\'information' },
  { to: '/productivite-equipe-outil', label: 'Productivité des équipes' },
];

const Header = () => {
  const location = useLocation();
  const isHome = location.pathname === '/';

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsResourcesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileResourcesOpen(false);
    setIsResourcesOpen(false);
  }, [location.pathname]);

  const homeNavLinks = [
    { href: '#problem', label: 'Le Problème' },
    { href: '#solution', label: 'La Solution' },
    { href: '#capabilities', label: 'Le Clone' },
    { href: '#use-cases', label: 'Cas Pratiques' },
    { href: '#faq', label: 'FAQ' },
  ];

  const handleCTA = () => {
    trackCTAClick('header_demo', window.location.pathname);
    if (isHome) {
      document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-[#09090b]/90 backdrop-blur-xl border-b border-white/5' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <img 
              src={LOGO_URL} 
              alt="CloneIA" 
              className="h-16 md:h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {isHome ? (
              <>
                {homeNavLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="text-[#a1a1aa] hover:text-[#f5f5f5] transition-colors text-sm font-medium"
                    data-testid={`nav-link-${link.href.slice(1)}`}
                  >
                    {link.label}
                  </a>
                ))}

                {/* Resources Dropdown — homepage only */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsResourcesOpen(!isResourcesOpen)}
                    className="flex items-center gap-1 text-[#a1a1aa] hover:text-[#f5f5f5] transition-colors text-sm font-medium"
                    data-testid="nav-resources-trigger"
                  >
                    Ressources
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isResourcesOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isResourcesOpen && (
                    <div 
                      className="absolute top-full right-0 mt-3 w-72 bg-[#0f0f17] border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden"
                      data-testid="resources-dropdown"
                    >
                      <div className="p-4 space-y-5">
                        {resourcesMenu.map((group) => (
                          <div key={group.category}>
                            <p className="text-[#3b82f6] text-xs uppercase tracking-[0.15em] font-bold mb-2">
                              {group.category}
                            </p>
                            <div className="space-y-0.5">
                              {group.links.map((link) => (
                                <Link
                                  key={link.to}
                                  to={link.to}
                                  onClick={() => setIsResourcesOpen(false)}
                                  className="block px-3 py-2 rounded-lg text-[#a1a1aa] hover:text-[#f5f5f5] hover:bg-[#1a1a2e]/60 transition-all text-sm"
                                  data-testid={`resource-link-${link.to.slice(1)}`}
                                >
                                  {link.label}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Internal pages — flat links + Ressources dropdown */
              <>
                {internalNavLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`text-sm font-medium transition-colors ${
                      location.pathname === link.to
                        ? 'text-[#f5f5f5]'
                        : 'text-[#a1a1aa] hover:text-[#f5f5f5]'
                    }`}
                    data-testid={`nav-link-${link.to === '/' ? 'accueil' : link.to.slice(1)}`}
                  >
                    {link.label}
                  </Link>
                ))}

                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsResourcesOpen(!isResourcesOpen)}
                    className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                      internalDropdownLinks.some(l => l.to === location.pathname)
                        ? 'text-[#f5f5f5]'
                        : 'text-[#a1a1aa] hover:text-[#f5f5f5]'
                    }`}
                    data-testid="nav-resources-trigger"
                  >
                    Ressources
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isResourcesOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isResourcesOpen && (
                    <div 
                      className="absolute top-full right-0 mt-3 w-64 bg-[#0f0f17] border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden"
                      data-testid="resources-dropdown"
                    >
                      <div className="p-3 space-y-0.5">
                        {internalDropdownLinks.map((link) => (
                          <Link
                            key={link.to}
                            to={link.to}
                            onClick={() => setIsResourcesOpen(false)}
                            className={`block px-3 py-2 rounded-lg transition-all text-sm ${
                              location.pathname === link.to
                                ? 'text-[#f5f5f5] bg-[#1a1a2e]/60'
                                : 'text-[#a1a1aa] hover:text-[#f5f5f5] hover:bg-[#1a1a2e]/60'
                            }`}
                            data-testid={`resource-link-${link.to.slice(1)}`}
                          >
                            {link.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </nav>

          {/* Desktop CTA */}
          {isHome ? (
            <Button
              onClick={handleCTA}
              className="hidden md:flex bg-[#3b82f6] hover:bg-[#2563eb] text-white px-6 py-2 rounded-full font-medium transition-all glow-blue-hover"
              data-testid="header-cta-button"
            >
              Demander une démo
            </Button>
          ) : (
            <Link to="/" className="hidden md:block">
              <Button
                className="bg-[#3b82f6] hover:bg-[#2563eb] text-white px-6 py-2 rounded-full font-medium transition-all glow-blue-hover"
                data-testid="header-cta-button"
              >
                Demander une démo
              </Button>
            </Link>
          )}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-[#f5f5f5] p-2"
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-[#09090b]/95 backdrop-blur-xl border-b border-white/5 py-4 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4 px-6">
              {isHome ? (
                <>
                  {homeNavLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="text-[#a1a1aa] hover:text-[#f5f5f5] transition-colors text-base font-medium py-2"
                    >
                      {link.label}
                    </a>
                  ))}

                  {/* Mobile Resources Accordion — homepage only */}
                  <button
                    onClick={() => setIsMobileResourcesOpen(!isMobileResourcesOpen)}
                    className="flex items-center justify-between text-[#a1a1aa] hover:text-[#f5f5f5] transition-colors text-base font-medium py-2"
                    data-testid="mobile-resources-trigger"
                  >
                    Ressources
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isMobileResourcesOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isMobileResourcesOpen && (
                    <div className="pl-4 space-y-4 pb-2" data-testid="mobile-resources-menu">
                      {resourcesMenu.map((group) => (
                        <div key={group.category}>
                          <p className="text-[#3b82f6] text-xs uppercase tracking-[0.15em] font-bold mb-2">
                            {group.category}
                          </p>
                          {group.links.map((link) => (
                            <Link
                              key={link.to}
                              to={link.to}
                              onClick={() => { setIsMobileMenuOpen(false); setIsMobileResourcesOpen(false); }}
                              className="block text-[#a1a1aa] hover:text-[#f5f5f5] transition-colors text-sm py-1.5"
                            >
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}

                  <Button
                    onClick={handleCTA}
                    className="bg-[#3b82f6] hover:bg-[#2563eb] text-white px-6 py-3 rounded-full font-medium mt-2"
                  >
                    Demander une démo
                  </Button>
                </>
              ) : (
                <>
                  {internalNavLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`text-base font-medium py-2 transition-colors ${
                        location.pathname === link.to
                          ? 'text-[#f5f5f5]'
                          : 'text-[#a1a1aa] hover:text-[#f5f5f5]'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}

                  <button
                    onClick={() => setIsMobileResourcesOpen(!isMobileResourcesOpen)}
                    className={`flex items-center justify-between text-base font-medium py-2 transition-colors ${
                      internalDropdownLinks.some(l => l.to === location.pathname)
                        ? 'text-[#f5f5f5]'
                        : 'text-[#a1a1aa] hover:text-[#f5f5f5]'
                    }`}
                    data-testid="mobile-resources-trigger-internal"
                  >
                    Ressources
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isMobileResourcesOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isMobileResourcesOpen && (
                    <div className="pl-4 space-y-1 pb-2">
                      {internalDropdownLinks.map((link) => (
                        <Link
                          key={link.to}
                          to={link.to}
                          onClick={() => { setIsMobileMenuOpen(false); setIsMobileResourcesOpen(false); }}
                          className={`block text-sm py-1.5 transition-colors ${
                            location.pathname === link.to
                              ? 'text-[#f5f5f5]'
                              : 'text-[#a1a1aa] hover:text-[#f5f5f5]'
                          }`}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}

                  <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button className="w-full bg-[#3b82f6] hover:bg-[#2563eb] text-white px-6 py-3 rounded-full font-medium mt-2">
                      Demander une démo
                    </Button>
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
